// @flow
import i18n from 'i18n-js';
import _ from 'lodash';
import moment from 'moment';
import type { Country } from 'types/country';
import { toFormattedDate, extractTimezone } from 'utils/time-utils';
import { DEFAULT_COUNTRY_ID } from 'config';

export default class ViewModelBuilder {
  object: any;
  formatTimestamps: Function;
  setLocalizedProperties: Function;
  convertToNumber: Function;
  convertStringToArray: Function;
  convertArrayToString: Function;
  setCityName: Function;
  setCountryName: Function;
  setLocationName: Function;
  setCitiesName: Function;

  build: Function;

  constructor(object: any) {
    this.object = Object.assign({}, object);
  }

  formatTimestamps(fields: Array<string> = ['createdDate', 'updatedDate']) {
    for (const fieldName of fields) {
      const timestamp: number = _.get(this.object, fieldName);

      if (timestamp) {
        _.set(this.object, `${fieldName}Pretty`, toFormattedDate(timestamp));
      } else {
        _.unset(this.object, `${fieldName}Pretty`);
      }
    }

    return this;
  }

  setLocalizedProperties(fields: Array<string>, locale: string = i18n.locale) {
    for (const fieldName of fields) {
      this.object[`${fieldName}Localized`] = _.get(this.object, `localization.${locale}.${fieldName}`);
    }

    return this;
  }

  convertToMomentObject(fields: Array<string>, format?: string) {
    for (const field of fields) {
      const value = _.get(this.object, field);
      _.set(this.object, field, value ? moment(value, format) : '');
    }

    return this;
  }

  // addTimezone updates the timezone for moment field but it doesn't the unix time
  addTimezone(fields: Array<string>, countryHash: Object, countryKey: number | string, tzKey: string = '') {
    if (this.object.timezone) {
      tzKey = this.object.timezone;
    }

    // change utc offset based on timezone
    this.object.utcOffset = extractTimezone(countryHash, countryKey, tzKey);
    if (!_.isEmpty(countryHash) && countryKey) {
      const country = _.get(countryHash, _.findKey(countryHash, ['code', countryKey])) || _.get(countryHash, `${countryKey}`);
      if (country) {
        const timeZoneOptions = country.timezones;
        const timeZoneObject = timeZoneOptions.find(timeZone => timeZone.offset === this.object.utcOffset);
        const timezoneName = _.get(timeZoneObject, 'tz');
        const timezone = _.get(timeZoneObject, 'code');
        this.object.timezoneName = timezoneName;
        this.object.timezone = timezone;
      }
    }

    for (const field of fields) {
      const value = _.get(this.object, field);
      _.set(this.object, field, value ? value.utcOffset(this.object.utcOffset) : '');
    }

    return this;
  }

  convertToEndOfDay(fields: Array<string>) {
    for (const field of fields) {
      const value = this.object[field];
      this.object[field] = value ? value.endOf('day') : '';
    }

    return this;
  }

  convertToStartOfDay(fields: Array<string>) {
    for (const field of fields) {
      const value = this.object[field];
      this.object[field] = value ? value.startOf('day') : '';
    }

    return this;
  }

  convertToNumber(fields: Array<string>) {
    for (const field of fields) {
      const rawValue = _.get(this.object, field, '').toString().trim();
      const value = Number(rawValue);
      if (rawValue === '' || isNaN(value)) {
        _.unset(this.object, field);
      } else {
        _.set(this.object, field, value);
      }
    }

    return this;
  }

  convertArrayStringToArrayNumber(fields: Array<string>, allowDuplicates: boolean = false) {
    for (const field of fields) {
      const rawArrayString = _.get(this.object, field, []);
      if (_.isArray(rawArrayString)) {
        let shouldSet = true;
        let arrayNumber = rawArrayString.map(rawString => {
          const value = Number(rawString);
          if (rawString === '' || isNaN(value)) {
            shouldSet = false;
          }
          return value;
        });
        if (!allowDuplicates) {
          arrayNumber = _.uniq(arrayNumber);
        }
        if (shouldSet === true) {
          _.set(this.object, field, arrayNumber);
        } else {
          _.unset(this.object, field);
        }
      }
    }

    return this;
  }

  convertStringToArray(fields: Array<string>, delimeter: string = '*') {
    const model = this.object;

    for (const field of fields) {
      const rawString = _.get(model, `${field}OnInput`, '');
      const convertedItems = _.compact(rawString.split(delimeter)).map(i => i.trim());

      _.set(model, field, convertedItems);
    }

    return this;
  }

  convertArrayToString(fields: Array<string>, delimeter: string = '*') {
    const model = this.object;

    for (const field of fields) {
      const fieldValues = _.get(model, field, []);
      const rawString = _.compact(fieldValues).reduce((result, item) => {
        return `${result}${delimeter} ${item}\n`;
      }, '');

      _.set(model, `${field}OnInput`, rawString);
    }

    return this;
  }

  undefineOptionalFields(fields: Array<string>) {
    const model = this.object;

    for (const field of fields) {
      if (_.isEmpty(_.get(model, field))) {
        _.set(model, field, undefined);
      }
    }

    return this;
  }

  setLocationName(locationHash: Object, field: string, locationType: string) {
    if (_.isEmpty(locationHash)) {
      return this;
    }

    const key = _.get(this.object, field);
    const name = _.get(_.get(locationHash, key) ||
      _.get(locationHash, _.find(locationHash, [field, key]))
      , 'name');
    const newFieldName = [..._.dropRight(field.split('.')), `${locationType}Name`];

    _.set(this.object, newFieldName, name);

    return this;
  }

  setCountryName(countryHash: Object, field: string) {
    return this.setLocationName(countryHash, field, 'country');
  }

  setCountryCodeName(countries: Array<Country>, field: string, locationType: string) {
    const key = _.get(this.object, field);
    const country = _.find(countries, ['code', key]);
    const name = _.get(country, 'name', '');

    const newFieldName = [..._.dropRight(field.split('.')), `${locationType}Name`];
    _.set(this.object, newFieldName, name);

    return this;
  }

  setCityName(cityHash: Object, field: string) {
    return this.setLocationName(cityHash, field, 'city');
  }

  setCitiesName(cityHash: Object, field: string) {
    const ids = _.get(this.object, field, []);
    if (_.isEmpty(cityHash) || !ids) {
      return this;
    }

    const names = ids.map(id => _.get(cityHash[id], 'name'));
    const newFieldName = [..._.dropRight(field.split('.')), 'cityNames'];

    _.set(this.object, newFieldName, names);

    return this;
  }

  setTaxiTypesName(taxiTypeHash: Object, field: string) {
    if (_.isEmpty(taxiTypeHash)) {
      return this;
    }

    const ids = _.get(this.object, field);
    const names = ids.map(id => _.get(taxiTypeHash[id], 'name'));
    const newFieldName = [..._.dropRight(field.split('.')), 'taxiTypeNames'];

    _.set(this.object, newFieldName, names);

    return this;
  }

  setLimitDisabled(field: string) {
    _.set(this.object, 'limitPerUserDisabled', _.get(this.object, field) === 0);
    return this;
  }

  setInvalidityStatusOnApp({ statusField, momentFields, awardableField }: Object) {
    const invalidityStatusOnApp = [];
    if (statusField && _.get(this.object, statusField) !== 'ACTIVE') {
      invalidityStatusOnApp.push(i18n.t('errors.invalid_on_app.status'));
    }
    if (momentFields && (moment() < _.get(this.object, momentFields[0]) || moment() > _.get(this.object, momentFields[1]))) {
      invalidityStatusOnApp.push(i18n.t('errors.invalid_on_app.date'));
    }
    if (awardableField && _.get(this.object, awardableField)) {
      invalidityStatusOnApp.push(i18n.t('errors.invalid_on_app.awardable'));
    }
    _.set(this.object, 'invalidityStatusOnApp', invalidityStatusOnApp);
    return this;
  }

  processByConfig(viewBuilderParams: Object) {
    const { momentFields, numberFields, countryIDField, cityIDField, cityIDsField, countriesState, countryCodeField, getViewBuilderParams } = viewBuilderParams;
    const countryHash = _.get(countriesState, 'countryHash');
    const cityHash = _.get(countriesState, 'cityHash');
    const countries = _.get(countriesState, 'entities');
    if (momentFields) {
      this.convertToMomentObject(momentFields);
      this.addTimezone(momentFields, countryHash, this.object.countryID || this.object.countryCode || DEFAULT_COUNTRY_ID);
      this.formatTimestamps(momentFields);
    }
    if (numberFields) {
      this.convertToNumber(numberFields);
    }
    if (countryIDField) {
      this.setCountryName(countryHash, countryIDField);
    }
    if (countryCodeField) {
      this.setCountryCodeName(countries, countryCodeField, 'country');
    }
    if (cityIDField) {
      this.setCityName(cityHash, cityIDField);
    }
    if (cityIDsField) {
      this.setCitiesName(cityHash, cityIDsField);
    }
    if (getViewBuilderParams) {
      this.processByConfig({ ...getViewBuilderParams(this.object), countriesState });
    }
    return this;
  }

  build() {
    return this.object;
  }
}
